import React, {forwardRef, useState} from 'react'
import {Box, Flex, Input, VisuallyHidden} from '@chakra-ui/react'
import PropTypes from 'prop-types'
import {useIntl} from 'react-intl'
import {remCalc} from '../../../../app/theme/_utils'

export const CustomInput = forwardRef(
    (
        {
            value,
            onClick,
            placeholder,
            calendarIcon,
            styles,
            isCalendarOpen,
            ariaInvalid,
            ariaDescribedBy,
            isFieldRequired,
        },
        ref,
    ) => {
        const {formatMessage} = useIntl()
        const hasValue = Boolean(value)
        const [focused, setFocused] = useState(false)
        const labelShouldFloat = !hasValue && (!isCalendarOpen || !focused)

        return (
            <Flex {...styles?.customInputContainer} position="relative">
                <Box
                    data-testid="placeholder-label"
                    {...(styles?.[labelShouldFloat ? 'defaultLabel' : 'activeLabel'] ?? {})}
                    position="absolute"
                    left={remCalc(16)}
                    transform={labelShouldFloat ? 'translateY(1px)' : 'translateY(-22px)'}
                    transition="all 0.2s ease"
                    pointerEvents="none"
                    zIndex="1"
                >
                    {placeholder}
                </Box>

                <VisuallyHidden>
                    <label htmlFor="datepicker-input">{placeholder}</label>
                </VisuallyHidden>

                <Input
                    id="datepicker-input"
                    ref={ref}
                    value={value}
                    readOnly
                    placeholder=""
                    tabIndex={0}
                    onClick={onClick}
                    onFocus={() => setFocused(true)}
                    onBlur={() => setFocused(false)}
                    aria-haspopup="dialog"
                    aria-expanded={isCalendarOpen}
                    aria-controls="calendar-popup"
                    aria-invalid={ariaInvalid}
                    aria-describedby={ariaDescribedBy}
                    data-testid="input"
                    aria-label={
                        placeholder ||
                        formatMessage({
                            id: 'date_picker.choose_date',
                            defaultMessage: 'Choose date',
                        })
                    }
                    onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault()
                            onClick()
                        }
                    }}
                    _focusVisible={{
                        outline: '2px solid',
                        outlineColor: 'blue.500',
                        outlineOffset: '2px',
                    }}
                    required={isFieldRequired}
                    {...styles?.customInput}
                />

                <Box data-testid="calendar-icon" {...styles?.calendarIconContainer}>
                    {calendarIcon}
                </Box>
            </Flex>
        )
    },
)

CustomInput.displayName = 'CustomInput'

CustomInput.propTypes = {
    value: PropTypes.string,
    onClick: PropTypes.func,
    placeholder: PropTypes.string,
    calendarIcon: PropTypes.node,
    styles: PropTypes.object,
    isCalendarOpen: PropTypes.bool,
    ariaInvalid: PropTypes.bool,
    ariaDescribedBy: PropTypes.string,
    isFieldRequired: PropTypes.bool | undefined,
}

export default CustomInput
